import React, { useEffect, useState } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { Modal, Button, Form, Row, Col } from "react-bootstrap";
import toast from "react-hot-toast";
import { companyID, employeeDetails } from "../../../../auth";
import createCustomSchema from "../../../../fieldValidator/fieldValidator";
import Loader from "./../../../../components/Loader";
import api from "../../../../api/axios";
import config from "../../../../config";

const PipActivationModal = ({
  show,
  handleClose,
  selectedEmployee,
  fetchCompanyEmployees,
}) => {
  const [companyResignationData, setCompanyResignationData] = useState({});
  const [details, setDetails] = useState({
    employee_id: "",
    employee: "",
    start_date: null,
    end_date: null,
    pip_reason: "",
    pip_duration_days: companyResignationData?.pip_duration_days,
    review_cycle_days: companyResignationData?.review_cycle_days,
    auto_termination: companyResignationData?.auto_termination,
    notify_days: companyResignationData?.notify_days,
    review_deadline_days: companyResignationData?.review_deadline_days,
    employee_department_notice_config_id: companyResignationData?.id || null,
    terminate_after_pip: null, // Stored as string "true" or "false" or null
  });

  const [errors, setErrors] = useState({});
  const [loader, setLoader] = useState(false);
  const loginEmployeeDetails = employeeDetails();

  const closeModal = () => {
    handleReset();
    handleClose();
  };

  const validationSchema = {
    start_date: createCustomSchema("date", {
      message: "Start date is required",
    }),
    end_date: createCustomSchema("date", {
      message: "End date is required",
    }),
    pip_reason: createCustomSchema("paragraph", {
      message: "Reason is required",
      min: 10,
    }),
    pip_duration_days: createCustomSchema("number", {
      message: "Duration is required",
      min: 1,
      customValidation: (value) => {
        if (companyResignationData?.pip_duration_days && value > companyResignationData.pip_duration_days) {
          throw new Error(`PIP duration cannot exceed ${companyResignationData.pip_duration_days} days`);
        }
        return true;
      },
    }),
    review_cycle_days: createCustomSchema("number", {
      message: "Review cycle is required",
      min: 1,
      customValidation: (value, details) => {
        if (value > details.pip_duration_days) {
          throw new Error("Review cycle must be less than PIP duration");
        }
        return true;
      },
    }),
    terminate_after_pip: {
      validate: (value, details) => {
        if (companyResignationData?.notice_period_days > details.pip_duration_days && (value !== "true" && value !== "false")) {
          throw new Error("Please select whether to terminate after PIP");
        }
        return true;
      },
    },

    leave_not_allow: {
      validate: (value, details) => {
        if (companyResignationData?.notice_period_days > details.pip_duration_days && (value !== "true" && value !== "false")) {
          throw new Error("Please select allow for leave or not");
        }
        return true;
      },
    },

    half_day_approval: {
      validate: (value, details) => {
        if (companyResignationData?.notice_period_days > details.pip_duration_days && (value !== "true" && value !== "false")) {
          throw new Error("Please select whether to take half day or not");
        }
        return true;
      },
    },
  };

  const validateField = async (key, value) => {
    try {
      if (validationSchema[key]) {
        await validationSchema[key].validate(value, details);
        setErrors((prevErrors) => ({ ...prevErrors, [key]: null }));
      }
    } catch (error) {
      setErrors((prevErrors) => ({ ...prevErrors, [key]: error.message }));
    }
  };

  useEffect(() => {
    if (details.start_date && details.pip_duration_days) {
      const newEndDate = new Date(details.start_date);
      newEndDate.setDate(
        newEndDate.getDate() + parseInt(details.pip_duration_days, 10)
      );
      const newReviewCycleDays = Math.floor(parseInt(details.pip_duration_days, 10) / 2) || 1;
      
      setDetails((prev) => ({
        ...prev,
        end_date: newEndDate,
        review_cycle_days: newReviewCycleDays,
      }));
      
      validateField("end_date", newEndDate);
      validateField("review_cycle_days", newReviewCycleDays);
    }
  }, [details.start_date, details.pip_duration_days]);

  const handleChange = (key, value) => {
    if (key === "pip_duration_days") {
      const maxDuration = companyResignationData?.pip_duration_days;
      if (maxDuration && value > maxDuration) {
        setErrors((prevErrors) => ({
          ...prevErrors,
          pip_duration_days: `PIP duration cannot exceed ${maxDuration} days`,
        }));
        return;
      }
    }
    if (key === "review_cycle_days") {
      if (value > details.pip_duration_days) {
        setErrors((prevErrors) => ({
          ...prevErrors,
          review_cycle_days: "Review cycle must be less than PIP duration",
        }));
        return;
      }
    }
    setDetails((prev) => ({ ...prev, [key]: value }));
    validateField(key, value);
  };

  const handleReset = () => {
    const pipDuration = companyResignationData?.pip_duration_days || 30;
    setDetails((prev) => ({
      ...prev,
      start_date: null,
      end_date: null,
      pip_reason: "",
      pip_duration_days: pipDuration,
      review_cycle_days: Math.floor(pipDuration / 2) || companyResignationData?.review_cycle_days || 15,
      auto_termination: companyResignationData?.auto_termination,
      notify_days: companyResignationData?.notify_days,
      review_deadline_days: companyResignationData?.review_deadline_days,
      employee_department_notice_config_id: companyResignationData?.employee_department_notice_config_id || companyResignationData?.id || null,
      terminate_after_pip: null,
    }));
    setErrors({});
  };

  const checkValidations = async () => {
    const newErrors = {};
    let hasError = false;

    await Promise.all(
      Object.keys(validationSchema).map(async (key) => {
        try {
          await validationSchema[key].validate(details[key], details);
          newErrors[key] = null;
        } catch (error) {
          newErrors[key] = error.message;
          hasError = true;
        }
      })
    );

    setErrors(newErrors);
    return !hasError;
  };

  const handleSubmit = async () => {
    setLoader(true);
    try {
      const valid = await checkValidations();
      if (!valid) return;

      const pipData = {
        activated_by: loginEmployeeDetails?.id,
        employee_id: details?.employee_id,
        start_date: details?.start_date,
        end_date: details?.end_date,
        pip_reason: details?.pip_reason,
        pip_duration_days: details?.pip_duration_days,
        review_cycle_days: details?.review_cycle_days,
        auto_termination: details?.auto_termination,
        notify_days: details?.notify_days,
        review_deadline_days: details?.review_deadline_days,
        employee_department_notice_config_id: details?.employee_department_notice_config_id,
        terminate_after_pip: details?.terminate_after_pip ? JSON.parse(details.terminate_after_pip) : null,
        leave_not_allow: details?.leave_not_allow ? JSON.parse(details.leave_not_allow) : null,
        half_day_approval: details?.half_day_approval ? JSON.parse(details.half_day_approval) : null,
      };
       const company_id = companyID();
      const response = await api.post(
        `${config.API_BASE_URL}api/pip-activate?company_id=${company_id}`,
        pipData
      );
      fetchCompanyEmployees();
      closeModal();
      toast.success(response?.data?.message || "PIP activated successfully");
    } catch (error) {
      closeModal();
      toast.error("Something went wrong while activating PIP.");
    } finally {
      setLoader(false);
      
    }
  };

  const getCompanyResignationDetail = async (employment_status, department_id) => {
    try {
      const company_id = companyID();
      const response = await api.get(
        `${config.API_BASE_URL}api/company-resignation-detail?employment_status=${employment_status}&department_id=${department_id}&company_id=${company_id}`
      );
      setCompanyResignationData(response?.data?.result[0]);

      if (show) {
        const today = new Date();
        const endDate = new Date();
        const pipDuration = response?.data?.result[0]?.pip_duration_days || 30;
        endDate.setDate(today.getDate() + pipDuration);

        setDetails({
          employee_id: selectedEmployee?.id ?? "",
          employee: `${selectedEmployee?.first_name} ${
            selectedEmployee?.middle_name ?? ""
          } ${selectedEmployee?.last_name}`,
          start_date: today,
          end_date: endDate,
          pip_reason: "",
          pip_duration_days: pipDuration,
          review_cycle_days: Math.floor(pipDuration / 2) || response?.data?.result[0]?.review_cycle_days || 15,
          auto_termination: response?.data?.result[0]?.auto_termination,
          notify_days: response?.data?.result[0]?.notify_days,
          review_deadline_days: response?.data?.result[0]?.review_deadline_days,
          employee_department_notice_config_id: response?.data?.result[0]?.id || null,
          terminate_after_pip: null,
        });
      }
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    getCompanyResignationDetail(selectedEmployee?.employment_status, selectedEmployee?.department_id);
  }, [selectedEmployee, show]);

  const isNoticePeriodGreater = companyResignationData?.notice_period_days > details.pip_duration_days;
  const isNoticePeriodEqual = companyResignationData?.notice_period_days === details.pip_duration_days;

  return (
    <Modal show={show} centered backdrop="static">
      <Modal.Header>
        <Modal.Title>Activate PIP for {details?.employee}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form>
          <Form.Group className="mb-3">
            <Form.Label>
              PIP Duration (days) <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              type="number"
              min={1}
              max={companyResignationData?.pip_duration_days}
              value={details.pip_duration_days}
              onChange={(e) =>
                handleChange("pip_duration_days", parseInt(e.target.value, 10))
              }
              placeholder="Enter duration in days"
            />
            {errors.pip_duration_days && (
              <Form.Text className="text-danger">
                * {errors.pip_duration_days}
              </Form.Text>
            )}
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>
              Review Cycle (days) <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              type="number"
              min={1}
              max={details.pip_duration_days - 1}
              value={details.review_cycle_days}
              onChange={(e) =>
                handleChange("review_cycle_days", parseInt(e.target.value, 10))
              }
              placeholder="Enter review cycle in days"
            />
            {errors.review_cycle_days && (
              <Form.Text className="text-danger">
                * {errors.review_cycle_days}
              </Form.Text>
            )}
          </Form.Group>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>
                  Start Date <span className="text-danger">*</span>
                </Form.Label>
                <DatePicker
                  selected={details.start_date}
                  onChange={(date) => handleChange("start_date", date)}
                  className="form-control"
                  placeholderText="Select start date"
                  dateFormat="dd-MM-yyyy"
                />
                {errors.start_date && (
                  <Form.Text className="text-danger">
                    * {errors.start_date}
                  </Form.Text>
                )}
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>
                  End Date <span className="text-danger">*</span>
                </Form.Label>
                <DatePicker
                  selected={details.end_date}
                  readOnly
                  className="form-control"
                  dateFormat="dd-MM-yyyy"
                />
                {errors.end_date && (
                  <Form.Text className="text-danger">
                    * {errors.end_date}
                  </Form.Text>
                )}
              </Form.Group>
            </Col>
          </Row>
          <Form.Group className="mb-3">
            <Form.Label>
              Reason / Comments <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              as="textarea"
              rows={4}
              value={details.pip_reason}
              onChange={(e) => handleChange("pip_reason", e.target.value)}
            />
            {errors.pip_reason && (
              <Form.Text className="text-danger">
                * {errors.pip_reason}
              </Form.Text>
            )}
          </Form.Group>
          {isNoticePeriodGreater && (
            <Form.Group className="mb-3">
              <Form.Label>
                Do you want to terminate after PIP? <span className="text-danger">*</span>
              </Form.Label>
              <div>
                <Form.Check
                  type="radio"
                  label="Yes"
                  name="terminate_after_pip"
                  value="true"
                  checked={details.terminate_after_pip === "true"}
                  onChange={() => handleChange("terminate_after_pip", "true")}
                  inline
                />
                <Form.Check
                  type="radio"
                  label="No"
                  name="terminate_after_pip"
                  value="false"
                  checked={details.terminate_after_pip === "false"}
                  onChange={() => handleChange("terminate_after_pip", "false")}
                  inline
                />
              </div>
              {errors.terminate_after_pip && (
                <Form.Text className="text-danger">
                  * {errors.terminate_after_pip}
                </Form.Text>
              )}
            </Form.Group>
          )}
          
          <Form.Group className="mb-3">
              <Form.Label>
                Do not allow to approve half day attendance ? <span className="text-danger">*</span>
              </Form.Label>
              <div>
                <Form.Check
                  type="radio"
                  label="Yes"
                  name="half_day_approval"
                  value="true"
                  checked={details.half_day_approval === "true"}
                  onChange={() => handleChange("half_day_approval", "true")}
                  inline
                />
                <Form.Check
                  type="radio"
                  label="No"
                  name="half_day_approval"
                  value="false"
                  checked={details.half_day_approval === "false"}
                  onChange={() => handleChange("half_day_approval", "false")}
                  inline
                />
              </div>
              {errors.half_day_approval && (
                <Form.Text className="text-danger">
                  * {errors.half_day_approval}
                </Form.Text>
              )}
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>
                Do not allow to take leave ? <span className="text-danger">*</span>
              </Form.Label>
              <div>
                <Form.Check
                  type="radio"
                  label="Yes"
                  name="leave_not_allow"
                  value="true"
                  checked={details.leave_not_allow === "true"}
                  onChange={() => handleChange("leave_not_allow", "true")}
                  inline
                />
                <Form.Check
                  type="radio"
                  label="No"
                  name="leave_not_allow"
                  value="false"
                  checked={details.leave_not_allow === "false"}
                  onChange={() => handleChange("leave_not_allow", "false")}
                  inline
                />
              </div>
              {errors.half_day_approval && (
                <Form.Text className="text-danger">
                  * {errors.half_day_approval}
                </Form.Text>
              )}
            </Form.Group>
            {isNoticePeriodEqual && (
            <Form.Text className="text-info">
              Employee doesn't have a notice period to serve; it will terminate automatically.
            </Form.Text>
          )}
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={closeModal} disabled={loader}>
          Close
        </Button>
        <Button
          variant="danger"
          type="submit"
          onClick={handleSubmit}
          disabled={loader}
        >
          {loader ? "Activating..." : "Activate PIP"}
        </Button>
      </Modal.Footer>
      <Loader show={loader} />
    </Modal>
  );
};

export default PipActivationModal;